import '../scss/app.scss';
import initSiteNavigation from './siteNavigation';
import KAUL_CHILD_THEME_Accordion from './controllers/Accordion';
import KAUL_CHILD_THEME_ActionCardReadMore from './controllers/ActionCardReadMore';
import KAUL_CHILD_THEME_SubscribeModal from './controllers/SubscribeModal';
import KAUL_CHILD_THEME_EventsCalendar from './controllers/EventsCalendar';

const sizeEventCards = () => {
    document.querySelectorAll('.kaul-event-grid').forEach((grid) => {
        const cards = Array.from(grid.querySelectorAll('.kaul-event-card'));
        const isStacked = window.matchMedia('(max-width: 767px)').matches;

        if (cards.length === 0) {
            return;
        }

        grid.style.removeProperty('--kaul-event-heading-height');
        cards.forEach((card) => card.style.removeProperty('--kaul-event-card-height'));

        const headingHeights = cards.map((card) => {
            const heading = card.querySelector('.kaul-event-card__heading');
            return heading instanceof HTMLElement ? heading.getBoundingClientRect().height : 0;
        });
        const sharedHeadingHeight = Math.ceil(Math.max(...headingHeights));

        if (!isStacked && sharedHeadingHeight > 0) {
            grid.style.setProperty('--kaul-event-heading-height', `${sharedHeadingHeight}px`);
        }

        const requiredHeights = cards.map((card) => {
            const content = card.querySelector('.kaul-event-card__content');
            const heading = card.querySelector('.kaul-event-card__heading');
            const summary = card.querySelector('.kaul-event-card__summary');
            const actions = card.querySelector('.kaul-event-card__actions');

            if (!(content instanceof HTMLElement) || !(heading instanceof HTMLElement) || !(summary instanceof HTMLElement) || !(actions instanceof HTMLElement)) {
                return 420;
            }

            const contentStyles = window.getComputedStyle(content);
            const gap = Number.parseFloat(contentStyles.rowGap) || 0;
            const topPadding = Number.parseFloat(contentStyles.paddingTop) || 0;
            const titleHeight = isStacked ? heading.getBoundingClientRect().height : sharedHeadingHeight;
            const summaryHeight = summary.getBoundingClientRect().height;
            const actionHeight = actions.getBoundingClientRect().height;

            return Math.ceil(topPadding + titleHeight + gap + summaryHeight + 16 + actionHeight);
        });
        const sharedCardHeight = Math.max(420, ...requiredHeights);

        cards.forEach((card, index) => {
            const height = isStacked ? Math.max(420, requiredHeights[index]) : sharedCardHeight;
            card.style.setProperty('--kaul-event-card-height', `${height}px`);
        });
    });
};

const initTestimonialCarousels = () => {
    document.querySelectorAll('[data-testimonial-carousel]').forEach((carousel) => {
        const slides = Array.from(carousel.querySelectorAll('[data-testimonial-slide]'));
        const previousButtons = Array.from(carousel.querySelectorAll('[data-testimonial-previous]'));
        const nextButtons = Array.from(carousel.querySelectorAll('[data-testimonial-next]'));
        const currentCounters = Array.from(carousel.querySelectorAll('[data-testimonial-current]'));
        const stage = carousel.querySelector('.kaul-testimonial__grid');

        if (slides.length < 2 || previousButtons.length === 0 || nextButtons.length === 0 || !(stage instanceof HTMLElement)) {
            return;
        }

        let activeIndex = 0;
        let transitionTimer = null;
        const measureSlideHeight = (slide) => {
            const slideStyles = window.getComputedStyle(slide);
            const slideBorders =
                (Number.parseFloat(slideStyles.borderTopWidth) || 0) +
                (Number.parseFloat(slideStyles.borderBottomWidth) || 0);

            return Math.ceil(slide.scrollHeight + slideBorders);
        };
        const resizeStage = () => {
            const activeSlide = slides[activeIndex];

            if (!(activeSlide instanceof HTMLElement)) {
                return;
            }

            const nextHeight = measureSlideHeight(activeSlide);
            if (nextHeight > 0) {
                stage.style.height = `${nextHeight}px`;
            }
        };

        const showSlide = (index) => {
            const currentHeight = stage.getBoundingClientRect().height;
            activeIndex = (index + slides.length) % slides.length;
            slides.forEach((slide, slideIndex) => {
                const isActive = slideIndex === activeIndex;
                slide.classList.toggle('is-active', isActive);
                slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
            });
            currentCounters.forEach((counter) => {
                if (counter instanceof HTMLElement) {
                    counter.textContent = String(activeIndex + 1);
                }
            });

            const targetHeight = measureSlideHeight(slides[activeIndex]);
            stage.style.height = `${Math.ceil(currentHeight)}px`;
            stage.classList.toggle('is-shrinking', targetHeight < currentHeight);
            carousel.classList.add('is-transitioning');
            window.requestAnimationFrame(() => {
                stage.style.height = `${targetHeight}px`;
            });

            if (transitionTimer !== null) {
                window.clearTimeout(transitionTimer);
            }
            transitionTimer = window.setTimeout(() => {
                carousel.classList.remove('is-transitioning');
                stage.classList.remove('is-shrinking');
            }, 650);
        };

        carousel.classList.add('is-carousel-ready');
        slides.forEach((slide) => {
            slide.hidden = false;
        });
        slides[0].classList.add('is-active');
        slides[0].setAttribute('aria-hidden', 'false');
        slides.slice(1).forEach((slide) => slide.setAttribute('aria-hidden', 'true'));
        resizeStage();

        previousButtons.forEach((button) => {
            button.addEventListener('click', () => showSlide(activeIndex - 1));
        });
        nextButtons.forEach((button) => {
            button.addEventListener('click', () => showSlide(activeIndex + 1));
        });
        window.addEventListener('resize', resizeStage, { passive: true });

        if ('ResizeObserver' in window) {
            const slideResizeObserver = new ResizeObserver(resizeStage);
            slides.forEach((slide) => slideResizeObserver.observe(slide));
        }

        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(resizeStage);
        }
    });
};

const initHeroParallax = () => {
    const heroSections = Array.from(document.querySelectorAll('.home .hero-header-section--background'));

    if (heroSections.length === 0) {
        return;
    }

    const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
    let rafId = null;

    const setHeroOffset = () => {
        rafId = null;

        if (reducedMotionMedia.matches) {
            heroSections.forEach((heroSection) => {
                heroSection.style.setProperty('--hero-header-parallax-y', '0px');
            });

            return;
        }

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;

        heroSections.forEach((heroSection) => {
            const rect = heroSection.getBoundingClientRect();
            const progress = Math.max(0, Math.min(1, (viewportHeight - rect.top) / (viewportHeight + rect.height)));
            const offset = (progress - 0.5) * 24;

            heroSection.style.setProperty('--hero-header-parallax-y', `${offset.toFixed(2)}px`);
        });
    };

    const requestHeroUpdate = () => {
        if (rafId !== null) {
            return;
        }

        rafId = window.requestAnimationFrame(setHeroOffset);
    };

    setHeroOffset();
    window.addEventListener('scroll', requestHeroUpdate, { passive: true });
    window.addEventListener('resize', requestHeroUpdate);

    if (typeof reducedMotionMedia.addEventListener === 'function') {
        reducedMotionMedia.addEventListener('change', requestHeroUpdate);
    } else if (typeof reducedMotionMedia.addListener === 'function') {
        reducedMotionMedia.addListener(requestHeroUpdate);
    }
};

document.addEventListener('DOMContentLoaded', () => {
    initSiteNavigation();

    // Initialize Accordions
    const accordionGroups = document.querySelectorAll('[data-accordion-group]');
    accordionGroups.forEach((group) => {
    new KAUL_CHILD_THEME_Accordion(group);
    });

    new KAUL_CHILD_THEME_ActionCardReadMore();
    new KAUL_CHILD_THEME_SubscribeModal();
    document.querySelectorAll('[data-kaul-calendar]').forEach((calendar) => {
        new KAUL_CHILD_THEME_EventsCalendar(calendar);
    });

    sizeEventCards();
    initHeroParallax();
    initTestimonialCarousels();

    if (document.fonts && typeof document.fonts.ready?.then === 'function') {
        document.fonts.ready.then(sizeEventCards);
    }
});

window.addEventListener('load', sizeEventCards);

let eventCardResizeTimer = null;
window.addEventListener('resize', () => {
    window.clearTimeout(eventCardResizeTimer);
    eventCardResizeTimer = window.setTimeout(sizeEventCards, 150);
});
